import React, { useState } from 'react';
import { useCountdown } from '../hooks/useCountdown';
import { X, Mail, Smartphone, ArrowRight, Sparkles, Calendar, ChevronRight, Lock, Eye, EyeOff, Loader2, AlertCircle } from 'lucide-react';
import { useLanguage } from '../i18nContext';
import { login, loginWithCode, loginWithPhone, sendVerificationCode, sendSmsCode } from '../services/auth';
import BrandLogo from './BrandLogo';
import { getErrorMessage } from '../utils/error';

interface LoginModalProps {
  onClose: () => void;
  onLogin: () => void;
  onSwitchToSignUp?: (prefill?: { method: 'email' | 'phone'; contact: string; code: string }) => void;
  onForgotPassword?: () => void;
  onNavigate?: (page: string) => void;
}

type LoginMethod = 'password' | 'emailCode' | 'phoneCode';

export default function LoginModal({ onClose, onLogin, onSwitchToSignUp, onForgotPassword, onNavigate }: LoginModalProps) {
  const [method, setMethod] = useState<LoginMethod>('password');
  const { t } = useLanguage();

  // 表单状态
  const [account, setAccount] = useState('');
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [emailCode, setEmailCode] = useState('');
  const [phone, setPhone] = useState('');
  const [phoneCode, setPhoneCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // 加载状态
  const [loading, setLoading] = useState(false);
  const [sendingEmailCode, setSendingEmailCode] = useState(false);
  const [sendingPhoneCode, setSendingPhoneCode] = useState(false);

  // 倒计时
  const [emailCountdown, startEmailCountdown] = useCountdown(60);
  const [phoneCountdown, startPhoneCountdown] = useCountdown(60);

  // 错误信息
  const [error, setError] = useState('');
  const [isShaking, setIsShaking] = useState(false);

  // 发送邮箱验证码
  const handleSendEmailCode = async () => {
    if (!email) {
      setError(t.auth.enterEmail);
      return;
    }
    setSendingEmailCode(true);
    setError('');
    try {
      await sendVerificationCode(email);
      startEmailCountdown();
    } catch (error: unknown) {
      setError(getErrorMessage(error, t.auth.sendFailed));
    } finally {
      setSendingEmailCode(false);
    }
  };

  // 发送手机验证码
  const handleSendPhoneCode = async () => {
    if (!phone) {
      setError(t.auth.enterPhone);
      return;
    }
    setSendingPhoneCode(true);
    setError('');
    try {
      await sendSmsCode(phone, 'login');
      startPhoneCountdown();
    } catch (error: unknown) {
      setError(getErrorMessage(error, t.auth.sendFailed));
    } finally {
      setSendingPhoneCode(false);
    }
  };

  // 处理登录
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (method === 'password') {
        if (!account || !password) {
          setError(t.auth.fillAllFields);
          setIsShaking(true);
          setTimeout(() => setIsShaking(false), 500);
          setLoading(false);
          return;
        }
        await login({ account, password });
      } else if (method === 'emailCode') {
        if (!email || !emailCode) {
          setError(t.auth.fillAllFields);
          setIsShaking(true);
          setTimeout(() => setIsShaking(false), 500);
          setLoading(false);
          return;
        }
        await loginWithCode({ email, verificationCode: emailCode });
      } else if (method === 'phoneCode') {
        if (!phone || !phoneCode) {
          setError(t.auth.fillAllFields);
          setIsShaking(true);
          setTimeout(() => setIsShaking(false), 500);
          setLoading(false);
          return;
        }
        await loginWithPhone({ phone, code: phoneCode });
      }
      onLogin();
    } catch (error: unknown) {
      setIsShaking(true);
      setTimeout(() => setIsShaking(false), 500);
      const msg = getErrorMessage(error, t.auth.loginFailed);
      if (msg.includes('USER_NOT_FOUND')) {
        if (method === 'emailCode') {
          onSwitchToSignUp?.({ method: 'email', contact: email, code: emailCode });
        } else if (method === 'phoneCode') {
          onSwitchToSignUp?.({ method: 'phone', contact: phone, code: phoneCode });
        } else {
          setError(t.auth.userNotFound);
        }
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  // 切换登录方式时清空错误
  const handleMethodChange = (newMethod: LoginMethod) => {
    if (newMethod !== method) {
      setMethod(newMethod);
      setError('');
    }
  };

  const methodsOrder: LoginMethod[] = ['password', 'emailCode', 'phoneCode'];

  const getSlideClass = (thisMethod: LoginMethod) => {
    if (method === thisMethod) {
      return 'opacity-100 translate-x-0 z-10 pointer-events-auto delay-100'; // Add slight delay to let outgoing slide out a bit
    }
    const thisIdx = methodsOrder.indexOf(thisMethod);
    const activeIdx = methodsOrder.indexOf(method);
    // If this tab is to the left of the active tab, it slides out to the left (-translate-x-8)
    // If it is to the right of the active tab, it slides out to the right (translate-x-8)
    if (thisIdx < activeIdx) {
      return 'opacity-0 -translate-x-8 z-0 pointer-events-none';
    } else {
      return 'opacity-0 translate-x-8 z-0 pointer-events-none';
    }
  }

  const methodLabels = t.auth.loginTabs;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 isolate">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm transform-gpu transition-opacity duration-300 pointer-events-auto"
        style={{ backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden' }}
        onClick={onClose}
      ></div>

      <div className="relative w-full max-w-5xl bg-white rounded-modal shadow-modal overflow-hidden flex flex-col md:flex-row h-auto max-h-[90vh] animate-in fade-in zoom-in-95 duration-500 border border-white z-10">

        {/* Left Side: Brand Panel */}
        <div className="w-full md:w-[45%] bg-surface-light p-8 md:p-10 flex flex-col relative overflow-hidden shrink-0 border-r border-surface-border">
          <div className="absolute top-[-50px] left-[-50px] w-80 h-80 bg-brand-200/30 rounded-full blur-[80px]"></div>
          <div className="absolute bottom-[-50px] right-[-50px] w-80 h-80 bg-prestige-300/20 rounded-full blur-[80px]"></div>
          <div className="absolute inset-0 bg-noise opacity-[0.03] pointer-events-none"></div>

          <div className="relative z-10 mb-12">
            <BrandLogo variant="light" className="mb-8" />
            <h1 className="text-4xl font-serif font-bold text-brand-900 mb-4 tracking-tight leading-tight">
              {t.auth.welcomeBack}
            </h1>
            <p className="text-ink-secondary text-lg font-normal opacity-80 max-w-[240px]">
              {t.auth.insightsWaiting}
            </p>
          </div>

          {/* What's New Section - Simplified for a cleaner editorial look */}
          <div className="relative z-10 mt-auto bg-white/40 backdrop-blur-md border border-surface-divider rounded-[32px] p-8 shadow-sm group/update hover:shadow-md transition-all duration-500">
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-1.5 text-xs font-bold text-brand-900 uppercase tracking-[0.15em]">
                <Sparkles size={12} fill="currentColor" className="opacity-80" />
                {t.auth.whatsNew}
              </div>
              <div className="flex items-center gap-1.5 text-xs font-semibold text-ink-muted uppercase tracking-[0.1em]">
                <Calendar size={14} className="opacity-60" />
                {t.auth.march2025}
              </div>
            </div>

            <div className="space-y-3">
              <h4 className="text-2xl font-serif font-bold text-brand-900 tracking-tight leading-tight flex flex-col gap-1">
                {t.auth.consultingEngineTitle.replace(/\s*v\d+\.\d+\s*/g, '')}
                <span className="text-[14px] font-sans font-bold text-brand-700/60 tracking-wider uppercase">
                  {t.auth.consultingEngineTitle.match(/v\d+\.\d+/)?.[0]}
                </span>
              </h4>
              <p className="text-[14px] text-ink-secondary leading-relaxed font-light opacity-90">
                {t.auth.consultingEngineDesc}
              </p>

              <div className="pt-2">
                <button
                  onClick={() => { onClose(); onNavigate?.('changelog'); }}
                  className="inline-flex items-center gap-1.5 py-1 text-xs font-bold text-brand-800 hover:text-brand-900 transition-all uppercase tracking-[0.1em] border-b border-brand-800/10 hover:border-brand-900/40"
                >
                  {t.auth.releaseNotes}
                  <ChevronRight size={14} className="group-hover/update:translate-x-0.5 transition-transform" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side: Login Form */}
        <div className="flex-1 p-8 md:p-10 bg-white flex flex-col justify-start relative overflow-y-auto">
          <button
            onClick={onClose}
            className="absolute top-5 right-5 p-2 text-ink-muted hover:text-brand-900 rounded-full hover:bg-surface-hover transition-all z-20"
          >
            <X size={20} />
          </button>

          {/* Title Area */}
          <div className="mb-4 text-center md:text-left mt-[68px]">
            <h2 className="text-4xl font-serif font-bold text-brand-900 mb-2">{t.auth.login}</h2>
            <p className="text-ink-muted text-sm font-light">{t.auth.welcomeSub}</p>
          </div>

          {/* 三种登录方式切换 */}
          <div className="flex p-1 bg-surface-DEFAULT rounded-full mb-6 border border-surface-border">
            <button
              type="button"
              onClick={() => handleMethodChange('password')}
              className={`flex-1 py-3 text-xs font-semibold tracking-wide rounded-full transition-all flex items-center justify-center gap-1.5
              ${method === 'password'
                  ? 'bg-white text-brand-900 shadow-sm ring-1 ring-surface-border'
                  : 'text-ink-muted hover:text-brand-900'}`}
            >
              <Lock size={14} /> {methodLabels.password}
            </button>
            <button
              type="button"
              onClick={() => handleMethodChange('emailCode')}
              className={`flex-1 py-3 text-xs font-semibold tracking-wide rounded-full transition-all flex items-center justify-center gap-1.5
              ${method === 'emailCode'
                  ? 'bg-white text-brand-900 shadow-sm ring-1 ring-surface-border'
                  : 'text-ink-muted hover:text-brand-900'}`}
            >
              <Mail size={14} /> {methodLabels.emailCode}
            </button>
            <button
              type="button"
              onClick={() => handleMethodChange('phoneCode')}
              className={`flex-1 py-3 text-xs font-semibold tracking-wide rounded-full transition-all flex items-center justify-center gap-1.5
              ${method === 'phoneCode'
                  ? 'bg-white text-brand-900 shadow-sm ring-1 ring-surface-border'
                  : 'text-ink-muted hover:text-brand-900'}`}
            >
              <Smartphone size={14} /> {methodLabels.phoneCode}
            </button>
          </div>

          <div className={`transition-all duration-300 overflow-hidden ${error ? 'max-h-16 opacity-100 mb-4' : 'max-h-0 opacity-0 mb-0'}`}>
            <div className="flex items-center gap-2 p-2.5 bg-brand-900 border border-transparent rounded-xl text-surface-light text-xs font-bold tracking-wide shadow-lg">
              <AlertCircle size={14} className="shrink-0" />
              {error}
            </div>
          </div>

          {/* Helper function to render verification fields */}
          {(() => {
            const renderVerificationFields = (type: 'emailCode' | 'phoneCode') => {
              const isEmail = type === 'emailCode';
              const label = isEmail ? t.auth.email : t.auth.phone;
              const inputValue = isEmail ? email : phone;
              const setInputValue = isEmail ? setEmail : setPhone;
              const codeValue = isEmail ? emailCode : phoneCode;
              const setCodeValue = isEmail ? setEmailCode : setPhoneCode;
              const handleSend = isEmail ? handleSendEmailCode : handleSendPhoneCode;
              const sending = isEmail ? sendingEmailCode : sendingPhoneCode;
              const countdown = isEmail ? emailCountdown : phoneCountdown;
              const inputType = isEmail ? 'email' : 'tel';

              return (
                <div className={`col-start-1 row-start-1 space-y-3 transition-all duration-300 ease-in-out transform ${getSlideClass(type)}`}>
                  <div className="space-y-1.5">
                    <label className="text-[11px] font-bold text-ink-muted uppercase tracking-widest ml-1 block">
                      {label}
                    </label>
                    <input
                      type={inputType}
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      placeholder=""
                      className="w-full h-11 px-4 rounded-lg border border-surface-border bg-surface-DEFAULT text-sm font-sans font-semibold focus:bg-white focus:border-surface-divider focus:ring-1 focus:ring-brand-700 transition-all outline-none"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[11px] font-bold text-ink-muted uppercase tracking-widest ml-1 block">
                      {t.auth.verificationCode}
                    </label>
                    <div className="flex gap-3">
                      <input
                        type="text"
                        value={codeValue}
                        onChange={(e) => setCodeValue(e.target.value)}
                        placeholder=""
                        className="flex-1 h-11 px-4 rounded-lg border border-surface-border bg-surface-DEFAULT text-sm font-sans font-semibold focus:bg-white focus:border-surface-divider focus:ring-1 focus:ring-brand-700 transition-all outline-none"
                      />
                      <button
                        type="button"
                        onClick={handleSend}
                        disabled={sending || countdown > 0}
                        className="px-4 h-11 bg-surface-DEFAULT border border-surface-border rounded-lg text-sm font-bold text-brand-900 hover:bg-surface-border disabled:opacity-50 disabled:cursor-not-allowed transition-all whitespace-nowrap min-w-[120px] flex items-center justify-center"
                      >
                        {sending ? (
                          <Loader2 size={16} className="animate-spin" />
                        ) : countdown > 0 ? (
                          `${countdown}s`
                        ) : (
                          t.auth.sendCode
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              );
            };

            return (
              <form className="relative grid grid-cols-1" onSubmit={handleSubmit}>
                {/* 密码登录表单 */}
                <div className={`col-start-1 row-start-1 space-y-3 transition-all duration-300 ease-in-out transform ${getSlideClass('password')}`}>
                  <div className="space-y-1.5">
                    <label className="text-[11px] font-bold text-ink-muted uppercase tracking-widest ml-1 block">
                      {t.auth.accountPlaceholder}
                    </label>
                    <input
                      type="text"
                      value={account}
                      onChange={(e) => setAccount(e.target.value)}
                      placeholder=""
                      className="w-full h-11 px-4 rounded-lg border border-surface-border bg-surface-DEFAULT text-sm font-sans font-semibold focus:bg-white focus:border-surface-divider focus:ring-1 focus:ring-brand-700 transition-all outline-none"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <div className="flex justify-between items-center px-1">
                      <label className="text-[11px] font-bold text-ink-muted uppercase tracking-widest">{t.auth.password}</label>
                      <button
                        type="button"
                        onClick={onForgotPassword}
                        className="text-[11px] font-medium text-ink-muted hover:text-brand-900 transition-colors"
                      >
                        {t.auth.forgotPassword}
                      </button>
                    </div>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder=""
                        className="w-full h-11 px-4 pr-12 rounded-lg border border-surface-border bg-surface-DEFAULT text-sm font-sans font-semibold focus:bg-white focus:border-surface-divider focus:ring-1 focus:ring-brand-700 transition-all outline-none"
                      />
                      {password.length > 0 && (
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-4 top-1/2 -translate-y-1/2 text-ink-muted hover:text-brand-900 transition-colors"
                        >
                          {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* 邮箱验证码登录表单 */}
                {renderVerificationFields('emailCode')}

                {/* 手机验证码登录表单 */}
                {renderVerificationFields('phoneCode')}

                {/* Spacer for the grid to stretch properly if password is conventionally smaller than others but since they all have 2 inputs now it matches */}
                <div className="col-start-1 row-start-2 pt-5 mt-1">
                  <button
                    type="submit"
                    disabled={loading}
                    className={`w-full relative h-12 ${isShaking ? 'animate-shake bg-brand-900 shadow-lg' : 'bg-brand-600 hover:bg-brand-700 shadow-cta-dark hover:shadow-2xl hover:-translate-y-0.5'} text-surface-light rounded-full text-lg font-medium tracking-wide transition-all duration-300 group flex items-center justify-center gap-3 overflow-hidden disabled:opacity-70 disabled:cursor-not-allowed`}
                  >
                    <div className="absolute inset-0 bg-noise opacity-10 mix-blend-overlay"></div>
                    {loading ? (
                      <Loader2 size={20} className="animate-spin relative z-10" />
                    ) : (
                      <>
                        <span className="relative z-10">{t.auth.login}</span>
                        <ArrowRight size={18} className="relative z-10 group-hover:translate-x-1 transition-transform stroke-[2.5]" />
                      </>
                    )}
                  </button>
                </div>
              </form>
            );
          })()}


          <div className="mt-6 text-center">
            <p className="text-ink-secondary text-sm font-light">
              {t.auth.noAccount}{' '}
              <button onClick={() => onSwitchToSignUp?.()} className="text-brand-900 font-semibold hover:underline ml-1">
                {t.auth.signup}
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}


